import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useLocation } from "react-router";
import { apiPost } from "../api";
import type { PortalConfig } from "../config";
import { date, money, phone } from "../format";

interface Invoice {
  ticketId: string; ticketNo: number; customerName: string; phone: string; email: string; address: string;
  lines: { name: string; quantity: number; unitPrice: string; total: string }[];
  insuredAmount: string | null; insuranceCost: string; subtotal: string; tax: string; total: string; paid: string;
  payment: { date: string | null; method: string; reference: string | null } | null;
}

/** Printable invoices, one per page, laid out like the old portal's printed invoice. */
export function PrintInvoices({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const location = useLocation();
  const ticketNos = (location.state as { ticketNos?: number[] } | null)?.ticketNos ?? [];
  const [invoices, setInvoices] = useState<(Invoice & { qr: string })[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketNos.length || !auth.user) return;
    apiPost<{ invoices: Invoice[] }>(config, auth.user.access_token, "/tickets/actions/invoices", { ticketNos })
      .then(async (r) => Promise.all(r.invoices.map(async (i) => ({
        ...i, qr: await QRCode.toDataURL(i.ticketId, { width: 180, margin: 2, errorCorrectionLevel: "H" }),
      }))))
      .then(setInvoices)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (invoices?.length) {
      const t = window.setTimeout(() => window.print(), 300);
      return () => window.clearTimeout(t);
    }
  }, [invoices]);

  if (!ticketNos.length) return <p className="no-print">Nothing to print. <Link to="/tickets">Back to tickets</Link></p>;

  return (
    <div className="print-page">
      <div className="no-print print-toolbar">
        <Link to="/tickets">← Back to tickets</Link>
        {invoices && <button type="button" className="primary" onClick={() => window.print()}>Print {invoices.length} invoice{invoices.length === 1 ? "" : "s"}</button>}
        {error && <span className="error">{error}</span>}
        {!invoices && !error && <span className="muted">Getting invoices ready…</span>}
      </div>
      {invoices?.map((inv) => {
        const due = Number(inv.total) - Number(inv.paid);
        return (
          <section key={inv.ticketNo} className="invoice">
            <header className="inv-head">
              <img src="/wrp-logo.jpg" alt="Watch Repair Professionals" height={110} />
              <p className="inv-shop">WRP<br />17100 W Bluemound Rd.<br />Ste 203<br />Brookfield WI 53005</p>
            </header>
            <div className="inv-rule" />
            <div className="inv-info">
              <div>
                <p className="inv-big">{inv.customerName}</p>
                <p className="inv-big">Ticket # {inv.ticketNo}</p>
                <p>{phone(inv.phone)}<br />{inv.email}<br />{inv.address}</p>
              </div>
              <div className="inv-amount">
                <p className="inv-big">USD: {money(inv.total)}</p>
                <div className="inv-small-rule" />
                <p className="inv-big">{due <= 0.005 ? "PAID" : `DUE ${money(due)}`}</p>
              </div>
            </div>
            <table className="inv-table">
              <thead><tr><th>SERVICE</th><th>QTY</th><th className="num">PRICE</th><th className="num">TOTAL</th></tr></thead>
              <tbody>
                {inv.lines.map((l, i) => (
                  <tr key={i}><th scope="row">{l.name}</th><td>{l.quantity}</td><td className="num">{money(l.unitPrice)}</td><td className="num">{money(l.total)}</td></tr>
                ))}
                {inv.insuredAmount && (
                  <tr><th scope="row">Insurance Amt of {money(inv.insuredAmount)}</th><td colSpan={2} /><td className="num">{money(inv.insuranceCost)}</td></tr>
                )}
                <tr><th scope="row">SubTotal</th><td colSpan={2} /><td className="num">{money(inv.subtotal)}</td></tr>
                {Number(inv.tax) > 0 && <tr><th scope="row">Total Tax</th><td colSpan={2} /><td className="num">{money(inv.tax)}</td></tr>}
                <tr><th scope="row">Total</th><td colSpan={2} /><td className="num">{money(inv.total)}</td></tr>
              </tbody>
            </table>
            <div className="inv-foot">
              <div>
                <p className="inv-big">Transaction Details</p>
                {inv.payment ? (
                  <p>Date: {date(inv.payment.date)}<br />Type: {inv.payment.method === "card" ? "Card" : inv.payment.method.replace(/_/g, " ")}
                    {inv.payment.reference && <><br />Transaction: {inv.payment.reference}</>}</p>
                ) : <p>No payment recorded.</p>}
              </div>
              <img src={inv.qr} alt="" className="inv-qr" />
            </div>
          </section>
        );
      })}
    </div>
  );
}
